import React, { useState, useEffect } from 'react';
import { ArrowRight, Calendar, Tag } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BlogSection = () => {
  const { language } = useLanguage();
  const sl = language === 'sl';
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await fetch(`${API_URL}/api/blog`);
        if (response.ok) {
          const data = await response.json();
          setPosts(data.slice(0, 3)); // Show only first 3 posts
        }
      } catch {
        setPosts([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPosts();
  }, []);

  const content = {
    sl: {
      label: 'DAVČNE NOVICE IN NASVETI',
      title: 'Bodite korak pred',
      titleHighlight: 'davčnimi spremembami.',
      description: 'Praktični nasveti, novosti iz zakonodaje in odgovori na pogosta vprašanja — vse, kar potrebujete za informirano poslovanje.',
      readMore: 'Preberite več',
      viewAll: 'Vse objave',
      categories: {
        'ddv': 'DDV',
        'ustanovitev': 'Ustanovitev',
        'dovoljenja': 'Dovoljenja',
        'place': 'Plače',
        'davcno': 'Davčno',
        'DDV': 'DDV',
        'Ustanovitev podjetja': 'Ustanovitev',
        'Delovna dovoljenja': 'Dovoljenja'
      }
    },
    en: {
      label: 'TAX NEWS AND TIPS',
      title: 'Stay one step ahead of',
      titleHighlight: 'tax changes.',
      description: 'Practical tips, legislative updates and answers to common questions — everything you need to run an informed business.',
      readMore: 'Read more',
      viewAll: 'All posts',
      categories: {
        'ddv': 'VAT',
        'ustanovitev': 'Formation',
        'dovoljenja': 'Permits',
        'place': 'Payroll',
        'davcno': 'Tax',
        'DDV': 'VAT',
        'Ustanovitev podjetja': 'Formation',
        'Delovna dovoljenja': 'Permits'
      }
    }
  };

  const t = content[language] || content.sl;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { day: 'numeric', month: 'long', year: 'numeric' };
    return date.toLocaleDateString(sl ? 'sl-SI' : 'en-US', options);
  };

  // Fallback posts if API returns empty
  const fallbackPosts = [
    {
      id: 'ddv-spremembe-2026',
      title: sl ? 'DDV spremembe v 2026: Kaj morate vedeti' : 'VAT changes in 2026: What you need to know',
      excerpt: sl ? 'Pregled glavnih sprememb DDV zakonodaje in kako vplivajo na vaše poslovanje v Sloveniji.' : 'Overview of main VAT legislation changes and how they affect your business in Slovenia.',
      date: new Date().toISOString(),
      category: 'DDV',
      image: '/images/exterior.webp'
    },
    {
      id: 'ustanovitev-doo-vodnik',
      title: sl ? 'Ustanovitev d.o.o. v Sloveniji: Korak za korakom' : 'Setting up a d.o.o. in Slovenia: Step by step',
      excerpt: sl ? 'Celoten postopek ustanovitve družbe z omejeno odgovornostjo v Sloveniji — od ideje do prvega računa.' : 'Complete process of setting up a limited liability company in Slovenia — from idea to first invoice.',
      date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
      category: 'Ustanovitev podjetja',
      image: '/images/meeting.webp'
    },
    {
      id: 'delovna-dovoljenja-faq',
      title: sl ? 'Delovna dovoljenja za tujce: Pogosta vprašanja' : 'Work permits for foreigners: FAQ',
      excerpt: sl ? 'Odgovori na najpogostejša vprašanja o delovnih dovoljenjih, enotnih dovoljenjih in postopkih v Sloveniji.' : 'Answers to the most common questions about work permits, single permits and procedures in Slovenia.',
      date: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
      category: 'Delovna dovoljenja',
      image: '/images/terrace.webp'
    }
  ];

  const displayPosts = posts.length > 0 ? posts : fallbackPosts;

  if (loading) {
    return (
      <section className="py-24 lg:py-32 px-6 lg:px-[120px] bg-[#F6F2EA]" data-testid="blog-section">
        <div className="max-w-[1440px] mx-auto">
          <div className="animate-pulse flex flex-col gap-8">
            <div className="h-12 bg-[#E2DACB] rounded w-1/3"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {['first', 'second', 'third'].map((skeletonId) => (
                <div key={skeletonId} className="bg-white p-6 space-y-4">
                  <div className="h-48 bg-[#E2DACB] rounded"></div>
                  <div className="h-6 bg-[#E2DACB] rounded w-3/4"></div>
                  <div className="h-4 bg-[#E2DACB] rounded w-full"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-24 lg:py-32 px-6 lg:px-[120px] bg-[#F6F2EA]" data-testid="blog-section" id="blog">
      <div className="max-w-[1440px] mx-auto">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:justify-between lg:items-end gap-8 mb-14">
          <div className="flex flex-col gap-5 max-w-[700px]">
            <span className="text-[13px] font-bold tracking-[2.5px] text-[#7A5B28]">{t.label}</span>
            <h2 className="font-serif text-3xl lg:text-[56px] leading-[1.02] tracking-[-1px] text-[#15181C]">
              {t.title} <span className="italic">{t.titleHighlight}</span>
            </h2>
            <p className="text-[17px] leading-[1.65] text-[#4E5359]">{t.description}</p>
          </div>
          
          <a 
            href="#blog" 
            className="self-start lg:self-auto flex items-center gap-2 text-[#7A5B28] font-semibold text-[15px] hover:text-[#5C431C] transition-colors group"
            data-testid="blog-view-all"
          >
            {t.viewAll}
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </a>
        </div>

        {/* Blog Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayPosts.map((post, index) => (
            <article 
              key={post.id}
              className="bg-[#FFFDF8] border border-[#E2DACB] overflow-hidden group"
              data-testid={`blog-card-${index + 1}`}
            >
              {/* Image */}
              <div className="aspect-[16/10] overflow-hidden">
                <picture>
                  <source srcSet={post.image} type="image/webp" />
                  <img 
                    src={post.image?.replace('.webp', '.jpg')} 
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </picture>
              </div>
              
              {/* Content */}
              <div className="p-6 flex flex-col gap-3">
                {/* Meta */}
                <div className="flex items-center gap-4 text-[13px] text-[#4E5359]">
                  <span className="flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5" />
                    {formatDate(post.date)}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Tag className="w-3.5 h-3.5" />
                    {t.categories[post.category] || post.category}
                  </span>
                </div>
                
                {/* Title */}
                <h3 className="font-serif text-[22px] leading-[1.25] text-[#15181C] group-hover:text-[#7A5B28] transition-colors">
                  {post.title}
                </h3>
                
                {/* Excerpt */}
                <p className="text-[15px] leading-[1.55] text-[#4E5359] line-clamp-2">
                  {post.excerpt}
                </p>
                
                {/* Read more link */}
                <a 
                  href={`#blog`}
                  className="mt-2 inline-flex items-center gap-2 text-[#7A5B28] font-semibold text-[14px] hover:text-[#5C431C] transition-colors group/link"
                >
                  {t.readMore}
                  <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
                </a>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default BlogSection;
